/**
 * Piano — «cosa si produce, quando, in che ordine».
 *
 * La sezione di lavoro. La testata segue la grammatica del tabellone di
 * Spedizioni: **i riquadri sono i filtri**, con i conteggi calcolati su tutto il
 * piano (non sulla selezione), così un numero non cambia mentre lo si guarda.
 * Sotto, la ricerca viva; poi la lista, o il Gantt come panoramica.
 */
import { Search, Upload, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { RigaTesta } from "@/components/RigaTesta";
import { cn } from "@/lib/utils";
import { GanttProduzione } from "@/pages/M2/Gantt";
import { num } from "@/pages/M2/format";
import { ListaPiano } from "@/pages/M2/ListaPiano";
import type { PianoResponse } from "@/types/m2";

interface Riquadro {
  chiave: string | null;
  label: string;
  n: number;
  allarme?: boolean;
}

export function PianoM2({
  piano,
  filtroIniziale,
  onCarica,
}: {
  piano: PianoResponse;
  /** Arriva dalle porte del Cruscotto: `cambi`, `sospette`, `stampo:T006`. */
  filtroIniziale?: string | null;
  onCarica: () => void;
}) {
  const k = piano.kpi;
  const [chiave, setChiave] = useState<string | null>(filtroIniziale ?? null);
  const [cerca, setCerca] = useState("");
  const [vista, setVista] = useState<"lista" | "gantt">("lista");

  // Il filtro arriva dall'URL (le porte del Cruscotto ci scrivono): se cambia a
  // pagina già montata va riletto, altrimenti la porta naviga senza filtrare —
  // il difetto trovato in Commesse il 16-08.
  useEffect(() => {
    setChiave(filtroIniziale ?? null);
  }, [filtroIniziale]);

  const riquadri: Riquadro[] = useMemo(() => {
    const base: Riquadro[] = [
      {
        chiave: null,
        label: "Tutte",
        n: k.lavorazioni_estrusione + k.lavorazioni_confezionamento,
      },
      { chiave: "estrusione", label: "Estrusione", n: k.lavorazioni_estrusione },
      { chiave: "confezionamento", label: "Confezionamento", n: k.lavorazioni_confezionamento },
      { chiave: "cambi", label: "Cambi stampo", n: k.cambi_stampo },
      {
        chiave: "sospette",
        label: "Durata non credibile",
        n: k.durate_sospette,
        allarme: true,
      },
    ];
    // Il filtro per stampo non è un riquadro fisso (sono 14): quando arriva dalla
    // classifica compare come sesto, così si vede che è attivo e si può togliere.
    if (chiave?.startsWith("stampo:")) {
      base.push({ chiave, label: `Stampo ${chiave.slice(7)}`, n: 0 });
    }
    return base;
  }, [k, chiave]);

  const filtro = useMemo(
    () => ({ chiave, cerca: cerca.trim().toLowerCase() }),
    [chiave, cerca]
  );

  return (
    <div className="space-y-3">
      <RigaTesta
        contesto={`${k.giorni_con_lavorazioni} giornate di lavoro`}
        caricatoIl={k.finestra_da}
        etichettaData="piano del"
        soloGiorno
        titoloBadge="Il piano viene da un export del MES: più invecchia, meno le date descrivono la produzione di adesso"
      >
        <span className="text-[11.5px] text-muted-foreground">MES · tipo «buono»</span>
      </RigaTesta>

      <div className="flex flex-wrap gap-2">
        {riquadri.map((r) => {
          const attivo = chiave === r.chiave;
          const spento = r.n === 0 && r.chiave !== null && !r.chiave.startsWith("stampo:");
          return (
            <button
              key={r.chiave ?? "tutte"}
              type="button"
              disabled={spento}
              onClick={() => setChiave(attivo ? null : r.chiave)}
              className={cn(
                "min-w-[7rem] rounded-lg border bg-card px-3 py-2 text-left transition-colors",
                attivo && "border-primary ring-1 ring-primary",
                // Un riquadro a zero si spegne, non sparisce: la testata non deve
                // cambiare forma sotto le mani di chi la usa (lezione di M3).
                spento ? "opacity-50" : "hover:bg-accent/40"
              )}
            >
              <span
                className={cn(
                  "block text-xl font-semibold leading-tight tabular-nums",
                  r.allarme && r.n > 0 && "text-red-700 dark:text-red-400"
                )}
              >
                {r.chiave?.startsWith("stampo:") ? "·" : num(r.n)}
              </span>
              <span className="mt-0.5 block text-[11px] text-muted-foreground">{r.label}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-[16rem] flex-1 items-center gap-2 rounded-lg border bg-card px-3 py-2">
          <Search className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            value={cerca}
            onChange={(e) => {
              setCerca(e.target.value);
              // La ricerca **azzera il riquadro** e guarda tutto il piano: un
              // meccanismo attivo alla volta, così non capita di non trovare una
              // riga perché era filtrata altrove. Stessa regola del tabellone.
              if (e.target.value) setChiave(null);
            }}
            placeholder="Cerca codice, descrizione, ordine MES, stampo…"
            className="min-w-0 flex-1 bg-transparent text-[12.5px] outline-none placeholder:text-muted-foreground"
          />
          {cerca && (
            <button
              type="button"
              onClick={() => setCerca("")}
              aria-label="Pulisci la ricerca"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          )}
        </label>
        <div className="inline-flex overflow-hidden rounded-lg border text-xs">
          {(["lista", "gantt"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVista(v)}
              className={cn(
                "px-3 py-2 font-medium",
                vista === v ? "bg-primary text-primary-foreground" : "bg-card hover:bg-accent/40"
              )}
            >
              {v === "lista" ? "Lista" : "Gantt"}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onCarica}
          className="inline-flex items-center gap-1.5 rounded-md border bg-card px-3 py-2 text-xs font-medium hover:bg-accent/40"
        >
          <Upload className="size-3.5" />
          Carica piano
        </button>
      </div>

      {vista === "lista" ? (
        <ListaPiano linee={piano.linee} filtro={filtro} />
      ) : (
        <GanttProduzione linee={piano.linee} />
      )}
    </div>
  );
}
